import { useState } from 'react';
import {
  CompanyLinkType,
  CompanyTagType,
  CompanyTagUnionType,
  mapCompanyLinkCategoriesType,
  mapCompanyLinkType,
  mapCompanySitesMinifiedType,
  useFindCompanyEmployeesSuspense,
  useFindCompanyLinkCategoriesSuspense,
  useFindCompanySitesMinifiedSuspense,
} from '@api';
import {
  AddCompanyFormSchemaType,
  AddCompanyModal,
  AddCompanySiteFormSchemaType,
  AddCompanySiteModal,
  CompanyLinkForm,
  CompanyLinkFormValuesType,
  CompanyLinkUpdateMutationArgsType,
  TouringDataTable,
  useCreateCompany,
  useCreateCompanySite,
} from '@common';
import { Modal } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useTranslation } from 'react-i18next';
import { useCompanyEmployeesTableColumns } from './hooks';

type CompanyEmployeesTablePropsType = {
  companyId: string;
  companyName: string;
  siteId: string;
  companyTags: CompanyTagType[];
  handleDeleteCompanyLink: (companyLinkId: string) => void;
  updateCompanyLink: (args: CompanyLinkUpdateMutationArgsType) => Promise<void>;
};

export const CompanyEmployeesTable: React.FC<
  CompanyEmployeesTablePropsType
> = ({
  companyId,
  companyName,
  siteId,
  companyTags,
  handleDeleteCompanyLink,
  updateCompanyLink,
}) => {
  const { t } = useTranslation('companyDetailsEmployees');
  const [employeeToEdit, setEmployeeToEdit] = useState<CompanyLinkType | null>(
    null
  );
  const [preselectCompanySiteId, setPreselectCompanySiteId] = useState<
    string | null
  >(null);
  const [
    addCompanyModalOpened,
    { open: openAddCompanyModal, close: closeAddCompanyModal },
  ] = useDisclosure(false);
  const [
    addCompanySiteModalOpened,
    { open: openAddCompanySiteModal, close: closeAddCompanySiteModal },
  ] = useDisclosure(false);

  const { data: records } = useFindCompanyEmployeesSuspense(companyId, siteId, {
    query: {
      select: (data) => mapCompanyLinkType(data.data),
    },
  });
  const { data: companySites } = useFindCompanySitesMinifiedSuspense({
    query: {
      select: (data) => mapCompanySitesMinifiedType(data?.data),
    },
  });
  const { data: companyLinkCategories } = useFindCompanyLinkCategoriesSuspense({
    query: {
      select: (data) => mapCompanyLinkCategoriesType(data.data),
    },
  });
  const { submitNewCompany } = useCreateCompany();
  const { createCompanySite } = useCreateCompanySite(companyId);

  const closeModal = () => {
    setEmployeeToEdit(null);
  };

  const handleEditCompanyEmployee = async (
    formValues: CompanyLinkFormValuesType
  ) => {
    if (!employeeToEdit) return;
    await updateCompanyLink({
      companyLink: employeeToEdit,
      data: formValues,
      onSuccessCallback: closeModal,
    });
  };

  const { columns } = useCompanyEmployeesTableColumns({
    onDeleteEmployee: handleDeleteCompanyLink,
    onEditEmployee: setEmployeeToEdit,
  });

  const handleSubmitNewCompany = async (data: AddCompanyFormSchemaType) => {
    const newCompany = await submitNewCompany({
      ...data,
      categories: data.categories as CompanyTagUnionType[],
      address: {
        ...data.address,
        cityEnglish: null,
        state: null,
      },
      contact: {
        ...data.contact,
        mobile: null,
      },
    });
    setPreselectCompanySiteId(newCompany.headquarter.id);
  };

  const handleSubmitNewCompanySite = async (
    formValues: AddCompanySiteFormSchemaType
  ): Promise<void> => {
    const newSite = await createCompanySite({
      address: {
        ...formValues.address,
        cityEnglish: null,
        state: null,
      },
      contact: {
        ...formValues.contact,
        mobile: null,
      },
    });
    setPreselectCompanySiteId(newSite.id);
  };

  return (
    <>
      <TouringDataTable<CompanyLinkType>
        columns={columns}
        records={records}
        withTableBorder={false}
        withColumnBorders={false}
      />
      <Modal
        opened={!!employeeToEdit}
        onClose={closeModal}
        title={t('MODAL.EDIT_EMPLOYEE_TITLE')}
        size="lg"
      >
        {employeeToEdit && (
          <CompanyLinkForm.Root
            key={employeeToEdit.linkId}
            companyLink={employeeToEdit}
            handleSubmit={handleEditCompanyEmployee}
            onCancel={closeModal}
          >
            <CompanyLinkForm.BusinessFields.Root>
              <CompanyLinkForm.BusinessFields.SiteSelect
                companySites={companySites}
                handleAddCompany={openAddCompanyModal}
                handleAddCompanySite={openAddCompanySiteModal}
                preselectSiteId={preselectCompanySiteId}
              />
              <CompanyLinkForm.BusinessFields.JobTitle />
              <CompanyLinkForm.BusinessFields.BuyerId />
            </CompanyLinkForm.BusinessFields.Root>
            <CompanyLinkForm.ContactFields />
            <CompanyLinkForm.Categories
              companyLinkCategories={companyLinkCategories}
            />
            <CompanyLinkForm.ExcludeFromConfirmationMail />
          </CompanyLinkForm.Root>
        )}
      </Modal>
      <AddCompanyModal
        companyTags={companyTags}
        opened={addCompanyModalOpened}
        onClose={closeAddCompanyModal}
        onSubmit={handleSubmitNewCompany}
      />
      <AddCompanySiteModal
        opened={addCompanySiteModalOpened}
        onClose={closeAddCompanySiteModal}
        onSubmit={handleSubmitNewCompanySite}
        companyName={companyName}
      />
    </>
  );
};
